<template>
  <div>
    <!-- Schedule tab temporarily hidden from the UI per admin request (still
         confusing/undecided how it should work). Backend, data, and methods
         are left intact below so it can be re-enabled later without rebuilding. -->

    <!-- ===================== REQUESTS TAB ===================== -->
    <div v-if="tab === 'requests'">
      <div class="ft-stat-row">
        <div class="ft-stat-box">
          <div class="ft-stat-icon ft-pending"><i class="fas fa-hourglass-half"></i></div>
          <div>
            <div class="ft-num">{{ counts.pending }}</div>
            <div class="ft-label">Pending</div>
          </div>
        </div>
        <div class="ft-stat-box">
          <div class="ft-stat-icon ft-approved"><i class="fas fa-check"></i></div>
          <div>
            <div class="ft-num">{{ counts.approved }}</div>
            <div class="ft-label">Approved</div>
          </div>
        </div>
        <div class="ft-stat-box">
          <div class="ft-stat-icon ft-rejected"><i class="fas fa-times"></i></div>
          <div>
            <div class="ft-num">{{ counts.rejected }}</div>
            <div class="ft-label">Rejected</div>
          </div>
        </div>
      </div>

      <div class="ft-alert-success" v-if="requestsSuccessMsg"><i class="fas fa-check-circle"></i> {{ requestsSuccessMsg }}</div>

      <div class="ft-card">
        <div class="ft-card-header-row">
          <h3 class="ft-card-title"><i class="fas fa-clipboard"></i> Factory Tour Requests</h3>
        </div>

        <form class="ft-filter-form" @submit.prevent="loadRequests(1)">
          <div class="ft-field">
            <label>Status</label>
            <select v-model="requestFilters.status">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div class="ft-field">
            <label>Visit Date From</label>
            <input type="date" v-model="requestFilters.date_from">
          </div>
          <div class="ft-field">
            <label>Visit Date To</label>
            <input type="date" v-model="requestFilters.date_to">
          </div>
          <div class="ft-field" style="min-width:220px;">
            <label>Search</label>
            <input type="text" v-model="requestFilters.search" placeholder="Name, institution, email">
          </div>
          <div class="ft-field">
            <button type="submit" class="ft-btn"><i class="fas fa-filter"></i> Filter</button>
          </div>
          <div class="ft-field">
            <button type="button" class="ft-btn ft-btn-outline" @click="resetRequestFilters"><i class="fas fa-undo"></i> Reset</button>
          </div>
        </form>

        <div class="ft-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Visit Date</th>
                <th>Full Name</th>
                <th>Institution</th>
                <th>Contact</th>
                <th>Participants</th>
                <th>Purpose</th>
                <th>Status</th>
                <th>Admin Notes</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in registrations" :key="row.id">
                <td>{{ formatDate(row.preferred_date) }}</td>
                <td>{{ row.full_name }}</td>
                <td>{{ row.institution_name }}</td>
                <td>{{ row.email }}<br>{{ row.phone_number }}</td>
                <td>{{ row.participants }}</td>
                <td style="max-width:220px;">{{ row.purpose || '-' }}</td>
                <td><span class="ft-badge" :class="'ft-badge-'+row.status">{{ row.status }}</span></td>
                <td style="max-width:180px;">{{ row.admin_notes || '-' }}</td>
                <td class="ft-action-cell">
                  <button class="ft-btn ft-btn-sm ft-btn-outline" @click="toggleEditRequest(row.id, $event)">
                    <i class="fas" :class="editingRequest === row.id ? 'fa-times' : 'fa-pencil-alt'"></i> {{ editingRequest === row.id ? 'Close' : 'Update' }}
                  </button>
                  <div v-if="editingRequest === row.id" class="ft-edit-panel" :style="editPanelStyle">
                    <label class="ft-field-label">Visit Date</label>
                    <input type="date" v-model="requestEditForm.preferred_date">
                    <label class="ft-field-label">Status</label>
                    <select v-model="requestEditForm.status">
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <textarea v-model="requestEditForm.admin_notes" rows="2" placeholder="Admin notes"></textarea>
                    <div class="ft-action-cell">
                      <button class="ft-btn ft-btn-sm" @click="saveRequestStatus(row.id)" :disabled="savingRequest"><i class="fas fa-check"></i> Save</button>
                      <button class="ft-btn ft-btn-sm ft-btn-danger" @click="removeRequest(row.id)"><i class="fas fa-trash"></i> Delete</button>
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="!registrations.length"><td colspan="9">
                <div class="ft-empty-state"><i class="fas fa-inbox"></i> No requests found.</div>
              </td></tr>
            </tbody>
          </table>
        </div>

        <div class="ft-pagination">
          <button :disabled="requestsPage <= 1" @click="loadRequests(requestsPage - 1)"><i class="fas fa-chevron-left"></i></button>
          <span class="ft-page-info">Page {{ requestsPage }} of {{ requestsLastPage }}</span>
          <button :disabled="requestsPage >= requestsLastPage" @click="loadRequests(requestsPage + 1)"><i class="fas fa-chevron-right"></i></button>
        </div>
      </div>
    </div>

    <!-- ===================== SCHEDULE TAB ===================== -->
    <div v-else>
      <div class="ft-alert-success" v-if="scheduleSuccessMsg"><i class="fas fa-check-circle"></i> {{ scheduleSuccessMsg }}</div>

      <div class="ft-card">
        <div class="ft-card-header-row">
          <h3 class="ft-card-title"><i class="fas fa-calendar-plus"></i> Add Visit Date</h3>
        </div>
        <form class="ft-filter-form" @submit.prevent="createSchedule">
          <div class="ft-field">
            <label>Visit Date</label>
            <input type="date" v-model="scheduleForm.visit_date" required>
          </div>
          <div class="ft-field">
            <label>Quota (participants)</label>
            <input type="number" v-model="scheduleForm.quota" min="1" max="500" required>
          </div>
          <div class="ft-field" style="min-width:220px;">
            <label>Notes</label>
            <input type="text" v-model="scheduleForm.notes" placeholder="Optional notes">
          </div>
          <div class="ft-field">
            <button type="submit" class="ft-btn" :disabled="creatingSchedule"><i class="fas fa-plus"></i> Add Schedule</button>
          </div>
        </form>
        <div class="ft-error-text" v-if="scheduleCreateError">{{ scheduleCreateError }}</div>
      </div>

      <div class="ft-card">
        <div class="ft-card-header-row">
          <h3 class="ft-card-title"><i class="fas fa-calendar-alt"></i> Available Dates</h3>
        </div>
        <div class="ft-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Visit Date</th>
                <th>Quota</th>
                <th>Booked</th>
                <th>Remaining</th>
                <th>Requests</th>
                <th>Notes</th>
                <th>Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in schedules" :key="row.id">
                <td>{{ formatDate(row.visit_date) }}</td>
                <td>{{ row.quota }}</td>
                <td>{{ row.booked_participants }}</td>
                <td>{{ row.remaining_quota }}</td>
                <td>{{ row.registrations_count }}</td>
                <td>{{ row.notes || '-' }}</td>
                <td><span class="ft-badge" :class="row.is_active ? 'ft-badge-approved' : 'ft-badge-rejected'">{{ row.is_active ? 'Active' : 'Inactive' }}</span></td>
                <td class="ft-action-cell">
                  <button class="ft-btn ft-btn-sm ft-btn-outline" @click="toggleEditSchedule(row.id, $event)">
                    <i class="fas" :class="editingSchedule === row.id ? 'fa-times' : 'fa-pencil-alt'"></i> {{ editingSchedule === row.id ? 'Close' : 'Edit' }}
                  </button>
                  <div v-if="editingSchedule === row.id" class="ft-edit-panel" :style="editPanelStyle">
                    <input type="number" v-model="scheduleEditForm.quota" min="1" max="500" placeholder="Quota">
                    <input type="text" v-model="scheduleEditForm.notes" placeholder="Notes">
                    <label style="font-size:12px; display:flex; align-items:center; gap:6px; font-weight:600; color:var(--ft-muted);">
                      <input type="checkbox" v-model="scheduleEditForm.is_active" style="width:auto;"> Active
                    </label>
                    <div class="ft-action-cell">
                      <button class="ft-btn ft-btn-sm" @click="saveSchedule(row.id)"><i class="fas fa-check"></i> Save</button>
                      <button class="ft-btn ft-btn-sm ft-btn-danger" @click="removeSchedule(row.id)"><i class="fas fa-trash"></i> Delete</button>
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="!schedules.length"><td colspan="8">
                <div class="ft-empty-state"><i class="fas fa-calendar-times"></i> No schedules yet.</div>
              </td></tr>
            </tbody>
          </table>
        </div>

        <div class="ft-pagination">
          <button :disabled="schedulePage <= 1" @click="loadSchedules(schedulePage - 1)"><i class="fas fa-chevron-left"></i></button>
          <span class="ft-page-info">Page {{ schedulePage }} of {{ scheduleLastPage }}</span>
          <button :disabled="schedulePage >= scheduleLastPage" @click="loadSchedules(schedulePage + 1)"><i class="fas fa-chevron-right"></i></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import registration from '@/services/adminRegistration.service';
import schedule from '@/services/adminSchedule.service';
import Api from '@/helpers/adminApi';
import moment from 'moment';

export default {
  name: 'AdminFactoryRequest',
  data() {
    return {
      tab: 'requests',

      // requests tab
      registrations: [],
      counts: { pending: 0, approved: 0, rejected: 0 },
      requestFilters: { status: '', date_from: '', date_to: '', search: '' },
      requestsPage: 1,
      requestsLastPage: 1,
      editingRequest: null,
      requestEditForm: { status: '', admin_notes: '', preferred_date: '' },
      savingRequest: false,
      requestsSuccessMsg: '',
      editPanelStyle: {},

      // schedule tab
      schedules: [],
      schedulePage: 1,
      scheduleLastPage: 1,
      scheduleForm: { visit_date: '', quota: 40, notes: '' },
      creatingSchedule: false,
      scheduleCreateError: '',
      editingSchedule: null,
      scheduleEditForm: { quota: '', notes: '', is_active: true },
      scheduleSuccessMsg: '',
    };
  },
  mounted() {
    this.loadRequests(1);
    this.loadSchedules(1);
  },
  methods: {
    // ---------- requests ----------
    loadRequests(page) {
      this.requestsPage = page;
      let context = this;
      Api(context, registration.index({ ...this.requestFilters, page })).onSuccess(function (response) {
        context.registrations = response.data.data.data;
        context.requestsLastPage = response.data.data.last_page;
        context.counts = response.data.counts;
      }).call();
    },
    resetRequestFilters() {
      this.requestFilters = { status: '', date_from: '', date_to: '', search: '' };
      this.loadRequests(1);
    },
    toggleEditRequest(id, event) {
      if (this.editingRequest === id) {
        this.editingRequest = null;
        return;
      }
      const row = this.registrations.find(r => r.id === id);
      this.requestEditForm = { status: row.status, admin_notes: row.admin_notes || '', preferred_date: row.preferred_date };
      this.editingRequest = id;
      this.positionEditPanel(event);
    },
    // Popover edit panels live inside table cells; absolutely-positioned
    // content there doesn't reliably stack above sibling content outside the
    // table across browsers, so we position it as position:fixed using the
    // trigger button's own coordinates instead of relying on CSS stacking.
    positionEditPanel(event) {
      const btn = event.target.closest('button');
      const rect = btn.getBoundingClientRect();
      const panelWidth = 230;
      let left = rect.right - panelWidth;
      if (left < 8) left = 8;
      this.editPanelStyle = { top: (rect.bottom + 8) + 'px', left: left + 'px' };
    },
    saveRequestStatus(id) {
      this.savingRequest = true;
      let context = this;
      Api(context, registration.updateStatus(id, this.requestEditForm)).onSuccess(function () {
        context.requestsSuccessMsg = 'Request status updated.';
        context.editingRequest = null;
        context.loadRequests(context.requestsPage);
        setTimeout(() => { context.requestsSuccessMsg = ''; }, 3000);
      }).onFinish(function () {
        context.savingRequest = false;
      }).call();
    },
    removeRequest(id) {
      if (!confirm('Delete this request?')) return;
      let context = this;
      Api(context, registration.delete(id)).onSuccess(function () {
        context.requestsSuccessMsg = 'Request deleted.';
        context.loadRequests(context.requestsPage);
        setTimeout(() => { context.requestsSuccessMsg = ''; }, 3000);
      }).call();
    },

    // ---------- schedule ----------
    loadSchedules(page) {
      this.schedulePage = page;
      let context = this;
      Api(context, schedule.index({ page })).onSuccess(function (response) {
        context.schedules = response.data.data.data;
        context.scheduleLastPage = response.data.data.last_page;
      }).call();
    },
    createSchedule() {
      this.creatingSchedule = true;
      this.scheduleCreateError = '';
      let context = this;
      Api(context, schedule.create(this.scheduleForm)).onSuccess(function () {
        context.scheduleForm = { visit_date: '', quota: 40, notes: '' };
        context.scheduleSuccessMsg = 'Schedule added.';
        context.loadSchedules(1);
        setTimeout(() => { context.scheduleSuccessMsg = ''; }, 3000);
      }).onError(function (err) {
        if (err.response && err.response.status === 422) {
          const errors = err.response.data.errors;
          context.scheduleCreateError = Object.values(errors)[0][0];
        } else {
          context.scheduleCreateError = 'Failed to add schedule.';
        }
      }).onFinish(function () {
        context.creatingSchedule = false;
      }).call();
    },
    toggleEditSchedule(id, event) {
      if (this.editingSchedule === id) {
        this.editingSchedule = null;
        return;
      }
      const row = this.schedules.find(r => r.id === id);
      this.scheduleEditForm = { quota: row.quota, notes: row.notes || '', is_active: !!row.is_active };
      this.editingSchedule = id;
      this.positionEditPanel(event);
    },
    saveSchedule(id) {
      let context = this;
      Api(context, schedule.update(id, this.scheduleEditForm)).onSuccess(function () {
        context.scheduleSuccessMsg = 'Schedule updated.';
        context.editingSchedule = null;
        context.loadSchedules(context.schedulePage);
        setTimeout(() => { context.scheduleSuccessMsg = ''; }, 3000);
      }).call();
    },
    removeSchedule(id) {
      if (!confirm('Delete this schedule?')) return;
      let context = this;
      Api(context, schedule.delete(id)).onSuccess(function () {
        context.scheduleSuccessMsg = 'Schedule deleted.';
        context.loadSchedules(context.schedulePage);
        setTimeout(() => { context.scheduleSuccessMsg = ''; }, 3000);
      }).call();
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },
  },
};
</script>
